import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { GrButtonComponent } from '@components/button';

import { GrModalService } from '@common/services';

import type { GrModalButton, GrModalConfig, GrModalResponse } from './types';

@UntilDestroy()
@Component({
  selector: 'gr-modal-footer',
  imports: [GrButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="gr-modal-footer flex w-full items-center gap-2"
      [class.justify-between]="hasLeft && hasRight"
      [class.justify-end]="!hasLeft && hasRight"
      [class.justify-start]="hasLeft && !hasRight"
    >
      @if (hasLeft) {
        <div class="flex flex-wrap items-center gap-2">
          @for (btn of leftButtons; track btn.key) {
            <gr-button
              [variant]="btn.variant ?? 'secondary'"
              [leadingIcon]="btn.leadingIcon ?? ''"
              [trailingIcon]="btn.trailingIcon ?? ''"
              [loading]="resolveLoading(btn)"
              [disabled]="resolveDisabled(btn)"
              [ariaLabel]="btn.ariaLabel ?? ''"
              (grClick)="onClick(btn)"
            >
              {{ btn.label }}
            </gr-button>
          }
        </div>
      }

      @if (hasRight) {
        <div class="flex flex-wrap items-center gap-2">
          @for (btn of rightButtons; track btn.key) {
            <gr-button
              [variant]="btn.variant ?? 'primary'"
              [leadingIcon]="btn.leadingIcon ?? ''"
              [trailingIcon]="btn.trailingIcon ?? ''"
              [loading]="resolveLoading(btn)"
              [disabled]="resolveDisabled(btn)"
              [ariaLabel]="btn.ariaLabel ?? ''"
              (grClick)="onClick(btn)"
            >
              {{ btn.label }}
            </gr-button>
          }
        </div>
      }
    </div>
  `,
})
export class GrModalFooterComponent {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly modalService = inject(GrModalService);

  protected readonly buttons: GrModalButton[];
  protected readonly leftButtons: GrModalButton[];
  protected readonly rightButtons: GrModalButton[];
  protected readonly hasLeft: boolean;
  protected readonly hasRight: boolean;

  private readonly buttonStates = computed(() => {
    const map: Record<string, ReturnType<GrModalService['getButtonState']>> = {};
    for (const btn of this.buttons) {
      map[btn.key] = this.modalService.getButtonState(this.ref, btn.key);
    }
    return map;
  });

  constructor() {
    const data = this.config.data as GrModalConfig | undefined;
    this.buttons = data?.buttons ?? [];
    this.leftButtons = this.buttons.filter((b) => b.align === 'left');
    this.rightButtons = this.buttons.filter((b) => (b.align ?? 'right') === 'right');
    this.hasLeft = this.leftButtons.length > 0;
    this.hasRight = this.rightButtons.length > 0;

    this.ref.onClose
      .pipe(untilDestroyed(this))
      .subscribe(() => this.modalService.unregister(this.ref));
    this.ref.onDestroy
      .pipe(untilDestroyed(this))
      .subscribe(() => this.modalService.unregister(this.ref));
  }

  protected resolveDisabled(btn: GrModalButton): boolean {
    return !!(btn.disabled || this.buttonStates()[btn.key]()?.disabled);
  }

  protected resolveLoading(btn: GrModalButton): boolean {
    return !!(btn.loading || this.buttonStates()[btn.key]()?.loading);
  }

  protected onClick(btn: GrModalButton): void {
    const cancelled = !!btn.cancel;
    const form = cancelled ? undefined : this.modalService.getFormValue(this.ref);
    const response: GrModalResponse = {
      buttonKey: btn.key,
      form,
      cancelled,
    };
    this.ref.close(response);
  }
}
